import React from "react";
import {useIncidentPre2021DeleteMutation, useIncidentRegistryQuery} from "../../query";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import {useTranslation} from "react-i18next";
import useResourceAuthorization from "../../../auth/useResourceAuthorization";
import {useIncidentSchema} from "../../../../schemaValidations/incidentPre2021Schema";
import {useFormik} from "formik";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import Card from "@mui/material/Card";
import MDBox from "../../../../materialDashboard/components/MDBox";
import MDTypography from "../../../../materialDashboard/components/MDTypography";
import Grid from "@mui/material/Grid";
import Localization from "./Localization";
import Conditions from "./Conditions";
import Incident from "./Incident";
import MDButton from "../../../../materialDashboard/components/MDButton";
import MDLoadingButton from "../../../../components/MDLoadingButton";
import AuditInfo from "../../../../components/AuditInfo";


const scrollMarginTop = "116px";

export const formikPropertyChanged = (prevProps, nextProps, properties) => {
    return properties.every(property =>
        (prevProps.formik.values[property] === nextProps.formik.values[property]
            && prevProps.formik.errors[property] === nextProps.formik.errors[property]
            && prevProps.formik.touched[property] === nextProps.formik.touched[property]
        ));
}

const IncidentForm = ({title, onSubmit, incident}) => {
    const incidentRegistry = useIncidentRegistryQuery();
    const incidentDeletionMutation = useIncidentPre2021DeleteMutation();
    const {askConfirmation} = useFeedbackTools();
    const {t} = useTranslation(['incidents', 'validation']);

    const authorization = useResourceAuthorization(incident);
    const validationSchema = useIncidentSchema({pre2021: true});

    const inputDisabled = !authorization.edit;
    const submitDisabled = !authorization.edit;

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            geom: {
                type: incident?.geom?.type ?? 'Point',
                coordinates: incident?.geom?.coordinates ?? [null, null]
            },

            // Localization
            date: incident?.date,
            workgroupId: incident?.workgroupId,
            regionId: incident?.regionId,
            provinceId: incident?.provinceId,
            locationName: incident?.locationName,
            timeRange: incident?.timeRange,
            elevationRelease: incident?.elevationRelease,
            aspect: incident?.aspect,

            // Conditions
            snowHeightMin: incident?.snowHeightMin,
            snowHeightMax: incident?.snowHeightMax,
            slopeAngleRelease: incident?.slopeAngleRelease,
            avalancheType: incident?.avalancheType,
            avalancheSizeV1: incident?.avalancheSizeV1,
            daysSinceLastSnowfall: incident?.daysSinceLastSnowfall,
            ww: incident?.ww,
            nearRidge: incident?.nearRidge,
            dangerScalePre93: incident?.dangerScalePre93,
            dangerScaleBulletin: incident?.dangerScaleBulletin,

            // Incident Data
            incidentCategoryV1: incident?.incidentCategoryV1,
            numberOfPeopleInvolved: incident?.numberOfPeopleInvolved,
            numberOfPeopleCaught: incident?.numberOfPeopleCaught,

            numberOfUninjured: incident?.numberOfUninjured,
            // Uninjured people info
            uninjuredCompletelyBuried: incident?.uninjuredCompletelyBuried,
            uninjuredPartiallyBuried: incident?.uninjuredPartiallyBuried,
            uninjuredShallowBuried: incident?.uninjuredShallowBuried,
            uninjuredFoundWithBeacon: incident?.uninjuredFoundWithBeacon,
            uninjuredFoundWithUcv: incident?.uninjuredFoundWithUcv,
            uninjuredFoundWithProbing: incident?.uninjuredFoundWithProbing,
            uninjuredFoundWithHearingSight: incident?.uninjuredFoundWithHearingSight,
            uninjuredSelfRescued: incident?.uninjuredSelfRescued,

            numberOfInjured: incident?.numberOfInjured,
            // Injured people info
            injuredCompletelyBuried: incident?.injuredCompletelyBuried,
            injuredPartiallyBuried: incident?.injuredPartiallyBuried,
            injuredShallowBuried: incident?.injuredShallowBuried,
            injuredFoundWithBeacon: incident?.injuredFoundWithBeacon,
            injuredFoundWithUcv: incident?.injuredFoundWithUcv,
            injuredFoundWithProbing: incident?.injuredFoundWithProbing,
            injuredFoundWithHearingSight: incident?.injuredFoundWithHearingSight,
            injuredSelfRescued: incident?.injuredSelfRescued,

            numberOfDeaths: incident?.numberOfDeaths,
            // Deaths info
            deadMissing: incident?.deadMissing,
            deadRecovered: incident?.deadRecovered,
            deadCompletelyBuried: incident?.deadCompletelyBuried,
            deadPartiallyBuried: incident?.deadPartiallyBuried,
            deadShallowBuried: incident?.deadShallowBuried,
            deadFoundWithBeacon: incident?.deadFoundWithBeacon,
            deadFoundWithUcv: incident?.deadFoundWithUcv,
            deadFoundWithProbing: incident?.deadFoundWithProbing,
            deadFoundWithHearingSight: incident?.deadFoundWithHearingSight,
            deadBySuffocation: incident?.deadBySuffocation,
            deadByTrauma: incident?.deadByTrauma,
            deadByOther: incident?.deadByOther,

            rescueType: incident?.rescueType,
            alpineRescue: incident?.alpineRescue,

            // Burial time and depth
            burialTimePerson1: incident?.burialTimePerson1,
            burialTimePerson2: incident?.burialTimePerson2,
            burialTimePerson3: incident?.burialTimePerson3,
            burialDepthPerson1: incident?.burialDepthPerson1,
            burialDepthPerson2: incident?.burialDepthPerson2,
            burialDepthPerson3: incident?.burialDepthPerson3,

            notes: incident?.notes,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => await onSubmit(values)
    });

    const handleDelete = async () => {
        const innerDeleteIncident = async () => {
            await incidentDeletionMutation.mutateAsync(incident.id);
        }
        await askConfirmation({
            title: t('incidentForm.deleteConfirmationTitle'),
            description: t('incidentForm.deleteDescription'),
            confirmationText: t('incidentForm.deleteConfirmationText'),
            onConfirm: innerDeleteIncident
        });
    }

    if (incidentRegistry.isLoading) {
        return <LoadingOverlay/>
    }

    const hasError = (formik.submitCount > 0 && !formik.isValid);

    return <>
        <Card sx={{mt: 4, px: 3, pb: 3}}>
            <MDBox variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                   mx={2} mt={-3} p={2} mb={1} textAlign="center">
                <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                    {title}
                </MDTypography>
            </MDBox>
            <Grid container rowSpacing={3} sx={{"& > *": {scrollMarginTop: scrollMarginTop}}}>
                <Grid item xs={12}>
                    <Localization formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled}
                                  incident={incident}/>
                </Grid>
                <Grid item xs={12}>
                    <Conditions formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <Incident formik={formik} incidentRegistry={incidentRegistry}
                              inputDisabled={inputDisabled}
                              numberOfPeopleCaught={formik.values.peopleCaught?.length ?? 0}/>
                </Grid>
                {
                    hasError && <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}} >
                        <MDTypography variant="button" color="error" fontWeight="regular">
                            {t('incidentForm.fixErrorsToProceed')}
                        </MDTypography>
                    </Grid>
                }

                <Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", gap: 2}}>
                    <div>
                        {
                            authorization.delete && <MDButton variant="outlined" color="error" onClick={handleDelete}>
                                {t('incidentForm.delete')}
                            </MDButton>
                        }
                    </div>
                    {
                        authorization.edit && <MDLoadingButton onClick={formik.handleSubmit}
                                                               name="saveButton"
                                                               label="save"
                                                               loading={formik.isSubmitting}
                                                               disabled={submitDisabled}
                                                               variant="gradient"
                                                               color="info">
                            {t('incidentForm.save')}
                        </MDLoadingButton>
                    }
                </Grid>
            </Grid>
        </Card>
        <AuditInfo {...incident} />
    </>
}


export default IncidentForm;